import { usePreviewMode } from "@/contexts/PreviewModeContext";
import { useSiteConfigSafe } from "@/contexts/SiteConfigContext";
import type { SiteConfig } from "@/types/sites";

interface DevSectionBadgeProps {
  sectionName: string;
  config?: SiteConfig;
  /** Override preview mode from context - useful when context is not available */
  isPreviewMode?: boolean;
}

/**
 * Dev-only floating badge that shows section and block names in preview mode.
 * Only visible when:
 * - Running in development environment (import.meta.env.DEV)
 * - Preview mode is active (either from prop or context)
 */
export function DevSectionBadge({ sectionName, config: configProp, isPreviewMode: isPreviewModeProp }: DevSectionBadgeProps) {
  const isPreviewModeContext = usePreviewMode();
  const siteConfigContext = useSiteConfigSafe();

  // Use prop if provided, otherwise fall back to context
  const isPreviewMode = isPreviewModeProp ?? isPreviewModeContext;

  // Only show in dev environment and preview mode
  if (!import.meta.env.DEV || !isPreviewMode) {
    return null;
  }

  // Use provided config or fall back to context
  const config = configProp ?? siteConfigContext?.config;

  // Get the block type from config based on section name
  const layoutConfig = config?.layout?.[sectionName as keyof typeof config.layout];
  const blockType =
    (layoutConfig as { blockConfig?: { blockType?: string } } | undefined)
      ?.blockConfig?.blockType || "default";

  return (
    <div
      className="absolute top-2 left-2 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-violet-600 text-white text-xs font-mono shadow-lg pointer-events-none select-none"
      style={{
        fontSize: "10px",
        lineHeight: "1.2",
        backdropFilter: "blur(4px)",
      }}
    >
      <span className="font-semibold uppercase tracking-wider">{sectionName}</span>
      <span className="opacity-60">|</span>
      <span className="opacity-80">{blockType}</span>
    </div>
  );
}

export default DevSectionBadge;
